import { compressImageFile } from "@components/Upload/compress-image";
import { shouldReuploadImagesOnPatch } from "@store/useReuploadImagesStore";

export const MULTIPART_IMAGE_KEYS = [
  "image",
  "icon",
  "banner",
  "profileImage",
  "logo",
] as const;

export function isHttpUrl(value: unknown): value is string {
  if (typeof value !== "string") return false;
  try {
    const u = new URL(value);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

/** Sync: File/Blob as-is; when preference on, keep URL strings so fetchData can hydrate. */
export function resolveUploadableImage(image: any): File | Blob | string | undefined {
  const candidate = image?.originFileObj ?? image;
  if (typeof File !== "undefined" && candidate instanceof File) return candidate;
  if (typeof Blob !== "undefined" && candidate instanceof Blob) return candidate;
  if (shouldReuploadImagesOnPatch() && isHttpUrl(candidate)) return candidate;
  return undefined;
}

/**
 * Existing image URL to send when a form keeps it in a sibling field (imageUrl,
 * bannerUrl) and clears the upload field. Returns undefined unless the
 * preference is on and the user did not pick a new file.
 */
export function reuploadFallbackUrl(
  current: unknown,
  existingUrl: unknown,
): string | undefined {
  if (!shouldReuploadImagesOnPatch()) return undefined;
  if (typeof File !== "undefined" && current instanceof File) return undefined;
  if (typeof Blob !== "undefined" && current instanceof Blob) return undefined;
  if (isHttpUrl(current)) return undefined;
  return isHttpUrl(existingUrl) ? existingUrl : undefined;
}

function guessFilename(url: string, contentType: string | null): string {
  try {
    const path = new URL(url).pathname;
    const base = path.split("/").pop() || "image";
    if (/\.(jpe?g|png|webp)$/i.test(base)) return base;
  } catch {
    /* ignore */
  }
  if (contentType?.includes("png")) return "image.png";
  if (contentType?.includes("webp")) return "image.webp";
  return "image.jpg";
}

const toFile = async (res: Response, url: string) => {
  const blob = await res.blob();
  const type = blob.type || res.headers.get("content-type") || "image/jpeg";
  return new File([blob], guessFilename(url, type), { type });
};

/** Hosts that reliably send Access-Control-Allow-Origin for public objects. */
function canFetchDirectly(url: string): boolean {
  try {
    return new URL(url).hostname.toLowerCase().endsWith(".supabase.co");
  } catch {
    return false;
  }
}

export async function urlToUploadableFile(url: string): Promise<File> {
  // S3 has no CORS for our origins — go straight to the same-origin proxy so the
  // browser never logs a blocked cross-origin fetch. Supabase public storage
  // allows CORS, so read it directly.
  if (canFetchDirectly(url)) {
    const direct = await fetch(url, { mode: "cors", credentials: "omit" });
    if (!direct.ok) {
      throw new Error(`Failed to fetch image for re-upload (${direct.status})`);
    }
    return compressImageFile(await toFile(direct, url));
  }

  const res = await fetch(`/api/proxy-image?url=${encodeURIComponent(url)}`);
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Failed to fetch image for re-upload (${res.status})${text ? `: ${text}` : ""}`,
    );
  }
  return compressImageFile(await toFile(res, url));
}

async function hydrateValue(value: unknown): Promise<unknown> {
  if (typeof File !== "undefined" && value instanceof File) return value;
  if (typeof Blob !== "undefined" && value instanceof Blob) return value;
  if (isHttpUrl(value)) return urlToUploadableFile(value);
  const nestedUrl = (value as { url?: unknown } | null)?.url;
  if (isHttpUrl(nestedUrl)) return urlToUploadableFile(nestedUrl);
  return value;
}

/** Convert known top-level image URL fields to File when preference is on. */
export async function hydrateMultipartImageFields<T extends Record<string, any>>(
  data: T,
): Promise<T> {
  if (
    typeof window === "undefined" ||
    !shouldReuploadImagesOnPatch() ||
    !data ||
    typeof data !== "object"
  ) {
    return data;
  }

  if (typeof FormData !== "undefined" && data instanceof FormData) {
    const form = data as FormData;
    for (const key of MULTIPART_IMAGE_KEYS) {
      const value = form.get(key);
      if (typeof value === "string" && isHttpUrl(value)) {
        const file = await urlToUploadableFile(value);
        form.set(key, file, file.name);
      }
    }
    return data;
  }

  const next: Record<string, any> = { ...data };
  await Promise.all(
    MULTIPART_IMAGE_KEYS.map(async (key) => {
      if (!(key in next)) return;
      next[key] = await hydrateValue(next[key]);
    }),
  );
  return next as T;
}
